"use client"

import { cn } from '@/shared/utils/tw'
import { useRealtimeAgentStore } from '@/shared/store/realtime-agent'
import { useCallback } from 'react'

interface SearchToggleButtonProps {
  className?: string
  onToggle?: (enabled: boolean) => void
}

/**
 * 웹 검색 ON/OFF 토글 버튼
 *
 * 인터넷 검색 활성화/비활성화
 */
export function SearchToggleButton({ className, onToggle }: SearchToggleButtonProps) {
  const isSearchEnabled = useRealtimeAgentStore((state) => state.isSearchEnabled)
  const setSearchEnabled = useRealtimeAgentStore((state) => state.setSearchEnabled)

  const handleToggle = useCallback(() => {
    const newValue = !isSearchEnabled
    setSearchEnabled(newValue)
    onToggle?.(newValue)
  }, [isSearchEnabled, setSearchEnabled, onToggle])

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={cn(
        "flex items-center justify-center gap-1.5",
        "px-3 py-1.5",
        "rounded-full",
        "text-xs font-medium",
        "transition-all duration-200",
        "border",
        isSearchEnabled
          ? "bg-blue-500/20 border-blue-500/50 text-blue-400"
          : "bg-[rgba(50,52,56,0.3)] border-white/10 text-white/60",
        "hover:opacity-80",
        "active:scale-95",
        className
      )}
      title={isSearchEnabled ? "웹 검색 끄기" : "웹 검색 켜기"}
    >
      <SearchIcon className="w-3.5 h-3.5" />
      <span>{isSearchEnabled ? "검색 ON" : "검색 OFF"}</span>
    </button>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  )
}

export default SearchToggleButton
